import { RecordingStatus } from "@/types/artifacts/Recordings";

/**
 * The single definition of "this recording's upload never finished" (LSP-17328).
 *
 * Two surfaces ask this question and they must agree: the lightbox shows a per-recording notice, and the
 * session page uses it to decide whether to offer Send Reminder — which sends a real SMS and email. They
 * were separate predicates for one commit, and they immediately disagreed: the lightbox required a
 * Ready/Completed status while the session gate did not, so during processing or an in-flight upload the
 * session offered to text a customer about a recording the lightbox was correctly staying quiet about.
 * A comment claiming the two "cannot disagree" is not a mechanism; one exported function is.
 *
 * Every branch fails CLOSED, because the expensive direction is showing a warning (and offering to
 * message someone) about a recording that was fine:
 *
 * - The server's explicit abandoned verdict includes finished-short recordings, even when
 *   `isUploadComplete` is true. Without that verdict, require `isUploadComplete === false`.
 * - A non-string `status` is not evidence the recording reached Ready/Completed. It also used to throw
 *   out of the lightbox predicate and take the whole render with it.
 * - A status that IS present and is neither Ready nor Completed means the recording is still processing
 *   (a clip merge, say). The player already says so, and an upload still in flight is not a failed one.
 * - A genuinely absent status keeps the original behavior and still counts, which is what the search
 *   index returns for older documents.
 *
 * Accepts either shape: lightbox artifacts carry `type`, search documents carry `entityType`.
 */
export type IncompleteUploadCandidate =
	| {
			// The RecordingSession id on a search document; on a lightbox artifact `id` is the MasterRecording id and the
			// session id travels as `legacyId` (mapResultToLightBoxArtifact).
			id?: unknown;
			legacyId?: unknown;
			type?: unknown;
			entityType?: unknown;
			status?: unknown;
			isUploadComplete?: unknown;
			isUploadAbandoned?: unknown;
	  }
	| null
	| undefined;

/**
 * The verdict vocabulary of contact-api's upload classifier (`UPLOAD_VERDICTS` in apps/contact-api/src/helpers/uploadVerdicts.ts),
 * carried on every upload-status item as `verdict`. Informational on this side: the admin never derives "abandoned" from it (the
 * server sends its own `abandoned` answer). The two apps share no package, so contact-api's `uploadStatusVerdictShapeGuard` reads
 * this literal and fails CI unless the two lists are set-equal, so the type below never misdescribes what the API sends.
 */
export const UPLOAD_VERDICTS = ["arriving", "quiet", "complete", "short", "failed", "lost", "cancelled"] as const;
export type UploadVerdict = (typeof UPLOAD_VERDICTS)[number];

/** One entry of `items` on GET /conversations/:id/upload-status. CLIP_MERGE sessions are never listed. */
export type UploadStatusItem = {
	kind: "recording" | "file" | "snapshot";
	id: string;
	/** Informational: the admin-policy classifier verdict. */
	verdict: UploadVerdict;
	/** Informational: createdVia "mobile" ⇒ mobile, anything else (including null) ⇒ web. */
	origin: "web" | "mobile";
	/**
	 * The server's admin-policy banner answer for this item (`isRecordingUploadAbandoned` for recordings, the web-reminder rule for
	 * stubs): the only field this predicate reads, and only for display.
	 */
	abandoned: boolean;
};

/** The slice of the upload-status response this predicate reads. `items` is absent on responses from before it shipped. */
export type UploadStatusVerdicts = { items?: UploadStatusItem[] | null } | null | undefined;

/**
 * The upload-status item for this recording, or undefined when the response has none (an older or cached response, a
 * recording of another conversation). A lightbox artifact is matched by its `legacyId` (the session id) first, a search
 * document by its `id`.
 */
function findRecordingUploadItem(item: NonNullable<IncompleteUploadCandidate>, uploadStatus: UploadStatusVerdicts) {
	const items = uploadStatus?.items;
	if (!Array.isArray(items) || items.length === 0) return undefined;
	const candidateIds = [item.legacyId, item.id].filter((id): id is string => typeof id === "string" && id !== "");
	for (const candidateId of candidateIds) {
		const match = items.find((entry) => entry?.kind === "recording" && entry.id === candidateId);
		if (match) return match;
	}
	return undefined;
}

/**
 * Does the upload-status response carry the server's `abandoned` answer for this row? The ONE match rule shared with
 * `isIncompleteRecordingUpload` (the session latch uses it to decide whether a "not incomplete" answer is definitive), so the
 * latch and the predicate can never disagree about which item belongs to a row.
 */
export function hasDefinitiveUploadItem(item: IncompleteUploadCandidate, uploadStatus: UploadStatusVerdicts): boolean {
	if (!item) return false;
	return typeof findRecordingUploadItem(item, uploadStatus)?.abandoned === "boolean";
}

export function isIncompleteRecordingUpload(item: IncompleteUploadCandidate, uploadStatus?: UploadStatusVerdicts): boolean {
	if (!item) return false;

	// `entityType` FIRST, `type` only as the fallback (break-it round 3). Either alone is unambiguous — a
	// search document carries `entityType`, a mapped lightbox artifact carries `type` — so precedence only
	// matters for a row carrying both and disagreeing. There it has to match what
	// `mapResultToLightBoxArtifact` does, and the mapper derives the artifact's `type` FROM `entityType`.
	// Preferring `type` meant the session (reading the raw document) and the lightbox (reading the mapped
	// artifact, whose `type` the mapper had just recomputed from `entityType`) classified one row two ways,
	// which is the exact property this shared predicate exists to guarantee. Refusing the row outright was
	// no better — it made the session silent while the lightbox still warned.
	const kind = item.entityType ?? item.type;
	if (kind !== "recording") return false;

	// LSP-17328: the per-item `abandoned` answer from GET /conversations/:id/upload-status OUTRANKS every field
	// below. It is the server's banner rule (the same `isRecordingUploadAbandoned` the index flag and hasAbandonedRecordingUpload
	// use), evaluated live over the response's one evidence snapshot, and the session page and the lightbox both pass the same
	// response, so they read one answer for one recording. Decided in both directions, and only on a real boolean: `verdict`
	// and `origin` are informational and never consulted here. An item without a boolean `abandoned` (or no item at all) falls
	// through to the legacy index-based predicate below, which fails closed.
	const uploadItem = findRecordingUploadItem(item, uploadStatus);
	if (uploadItem?.abandoned === true) return true;
	if (uploadItem?.abandoned === false) return false;

	if (item.status !== undefined && item.status !== null && typeof item.status !== "string") return false;

	// The server also marks finished-short recordings abandoned: the browser finished, but chunks are
	// missing. Neither isUploadComplete nor the transcode status can override that explicit verdict.
	if (item.isUploadAbandoned === true) return true;

	// Without a positive server verdict, unknown completion still fails closed.
	if (item.isUploadComplete !== false) return false;

	// A negative server verdict suppresses warnings even if a partial transcode is already COMPLETED
	// while the client is still uploading. Only real booleans decide whether to offer a reminder.
	if (item.isUploadAbandoned === false) return false;

	// No server verdict (an older index document, or a surface that doesn't plumb the field): fall back to
	// the original status gate unchanged, so every prior exemption still holds.
	const status = typeof item.status === "string" ? item.status.toUpperCase() : undefined;
	if (status && status !== RecordingStatus.Ready.toUpperCase() && status !== RecordingStatus.Completed.toUpperCase()) {
		return false;
	}

	return true;
}
